import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layouts/AdminLayout';
import {
  Search,
  Plus,
  FolderKanban,
  Calendar,
  Users,
  MoreVertical,
  ChevronRight
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';

const ProjectsPage = () => {
  const { api } = useAuth();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [newProject, setNewProject] = useState({
    client_id: '',
    name: '',
    services: [],
    target_date: '',
  });

  const services = [
    { id: 'social_media', label: 'Social Media' },
    { id: 'podcast', label: 'Podcast' },
    { id: 'video_content', label: 'Video Content' },
    { id: 'branding', label: 'Branding' },
    { id: 'web', label: 'Web Development' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, clientsRes] = await Promise.all([
        api.get('/projects'),
        api.get('/clients'),
      ]);
      setProjects(projectsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createProject = async (e) => {
    e.preventDefault();
    try {
      await api.post('/projects', newProject);
      setCreateOpen(false);
      setNewProject({
        client_id: '',
        name: '',
        services: [],
        target_date: '',
      });
      fetchData();
    } catch (error) {
      console.error('Failed to create project:', error);
    }
  };

  const toggleService = (serviceId) => {
    setNewProject(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(s => s !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.company_name || 'Unknown';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'completed': return 'bg-blue-500';
      case 'paused': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    getClientName(project.client_id).toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-syne font-bold text-white">Projects</h1>
            <p className="text-fp-textMuted text-sm mt-1">{projects.length} total projects</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fp-textMuted" />
              <Input
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-fp-surface border-white/10 text-white w-64"
                data-testid="projects-search"
              />
            </div>
            <Button
              onClick={() => setCreateOpen(true)}
              className="btn-primary"
              data-testid="create-project-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
          </div>
        </div>

        {/* Projects List */}
        <div className="space-y-4">
          {filteredProjects.map((project) => (
            <motion.div
              key={project.id}
              className="bg-fp-surface border border-white/5 rounded-sm p-6 hover:border-primary/30 transition-colors"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid={`project-card-${project.id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/20 rounded-sm flex items-center justify-center flex-shrink-0">
                    <FolderKanban className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-white">{project.name}</h3>
                      <span className={`w-2 h-2 rounded-full ${getStatusColor(project.status)}`} />
                    </div>
                    <p className="text-fp-textMuted text-sm">{getClientName(project.client_id)}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {project.services?.map((service) => (
                        <span
                          key={service}
                          className="px-2 py-1 bg-white/5 text-fp-textMuted text-xs rounded"
                        >
                          {service.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-2xl font-syne font-bold text-primary">{project.progress || 0}%</p>
                    <p className="text-fp-textMuted text-xs">Progress</p>
                  </div>
                  <div className="w-32">
                    <Progress value={project.progress || 0} className="h-2 bg-white/10" />
                  </div>
                  <div className="flex items-center gap-2 text-fp-textMuted text-sm">
                    <Calendar className="w-4 h-4" />
                    <span>{project.target_date || 'No deadline'}</span>
                  </div>
                  <Button variant="ghost" className="text-fp-textMuted hover:text-white">
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Create Project Modal */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="bg-fp-surface border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white font-syne">Create New Project</DialogTitle>
            </DialogHeader>
            <form onSubmit={createProject} className="space-y-4">
              <div>
                <Label className="text-fp-textMuted">Client</Label>
                <Select
                  value={newProject.client_id}
                  onValueChange={(value) => setNewProject({ ...newProject, client_id: value })}
                >
                  <SelectTrigger className="mt-1 bg-fp-bg border-white/10 text-white" data-testid="select-client">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent className="bg-fp-surface border-white/10">
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-fp-textMuted">Project Name</Label>
                <Input
                  value={newProject.name}
                  onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                  className="mt-1 bg-fp-bg border-white/10 text-white"
                  required
                  data-testid="input-project-name"
                />
              </div>
              <div>
                <Label className="text-fp-textMuted">Services</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {services.map((service) => (
                    <label
                      key={service.id}
                      className={`flex items-center gap-2 p-3 border rounded-sm cursor-pointer ${
                        newProject.services.includes(service.id)
                          ? 'border-primary bg-primary/10'
                          : 'border-white/10'
                      }`}
                    >
                      <Checkbox
                        checked={newProject.services.includes(service.id)}
                        onCheckedChange={() => toggleService(service.id)}
                      />
                      <span className="text-sm text-white">{service.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-fp-textMuted">Target Date</Label>
                <Input
                  type="date"
                  value={newProject.target_date}
                  onChange={(e) => setNewProject({ ...newProject, target_date: e.target.value })}
                  className="mt-1 bg-fp-bg border-white/10 text-white"
                  data-testid="input-target-date"
                />
              </div>
              <Button type="submit" className="w-full btn-primary" data-testid="submit-project-btn">
                Create Project
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default ProjectsPage;
